import React, { useState, useCallback } from 'react';
import {
  TextField,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { FileEdit } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../reducers';
import { updateTitle } from '../../selectedVideoSlice';
import type { FileType } from '../../../../types';

interface RenameFileModalProps {
  onClose: () => void;
}

const RenameFileModal: React.FC<RenameFileModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { globalModals } = useSelector((state: RootState) => state);
  console.log('Debug globalModals:', {
    globalModals,
    actionData: globalModals.actionData,
  });
  const { files = [], status } = globalModals.actionData || {};
  const file = files[0] as FileType;
  const { _id: fileId, name: currentName } = file || {};
  const [newName, setNewName] = useState(currentName);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState('');
  const isLoading = status === 'pending';

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setNewName(value);
    },
    []
  );

  const handleConfirm = () => {
    if (isLoading) return;
    dispatch(updateTitle({ fileId, title: newName.trim() }));
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          pt: 3,
          pb: 2,
          fontSize: '1.5rem',
          fontWeight: 600,
        }}
      >
        {t('selectedVideo.actions.modals.renameFile.title', {
          fileName: currentName,
        })}
      </DialogTitle>
      <DialogContent
        sx={{
          px: 4,
          pb: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t('selectedVideo.actions.modals.renameFile.description')}
        </Typography>
        <TextField
          id="new-file-name"
          label={t('selectedVideo.actions.modals.renameFile.label')}
          value={newName}
          onChange={handleNameChange}
          onKeyPress={handleKeyPress}
          error={Boolean(error)}
          disabled={isLoading}
          autoFocus
          fullWidth
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          color="secondary"
          disabled={isLoading}
          sx={{
            borderRadius: '24px',
            textTransform: 'none',
            px: 3,
            py: 1,
            minWidth: '100px',
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={Boolean(error) || !newName || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <FileEdit />}
          sx={{
            borderRadius: '24px',
            textTransform: 'none',
            px: 3,
            py: 1,
            minWidth: '100px',
          }}
        >
          {isLoading
            ? t('selectedVideo.actions.modals.renameFile.renaming')
            : t('common.rename')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameFileModal;
